import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Tag, Share2, Check, Copy } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, BACKEND_URL } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { SEO, ArticleStructuredData } from '@/components/common/SEO';

const ShareButton = ({ slug, title }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${BACKEND_URL}/api/share/news/${slug}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url: shareUrl });
      } catch (e) {
        console.error('Failed to share article:', e);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Fallback
        const input = document.createElement('input');
        input.value = shareUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleShare}
      className="border-gray-600 text-gray-300 hover:text-white hover:border-sky-400 transition-colors"
      data-testid="share-article-btn"
    >
      {copied ? <Check className="w-4 h-4 mr-1.5 text-green-400" /> : <Share2 className="w-4 h-4 mr-1.5" />}
      {copied ? 'Link Copied!' : 'Share'}
    </Button>
  );
};

export const NewsArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadArticle = async () => {
      try {
        setLoading(true);
        const [articleRes, leaguesRes, allNewsRes] = await Promise.all([
          api.getNewsArticle(slug),
          api.getLeagues(),
          api.getNews({ limit: 10 })
        ]);
        setArticle(articleRes.data);
        setLeagues(leaguesRes.data);
        
        // Get related articles (same league, excluding current)
        const related = allNewsRes.data
          .filter(a => a.slug !== slug)
          .slice(0, 3);
        setRelatedArticles(related);
      } catch (err) {
        console.error('Error loading article:', err);
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    loadArticle();
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLeagueName = (leagueId) => {
    const league = leagues.find(l => l.id === leagueId);
    return league?.name || '';
  };

  // Get plain text excerpt for SEO description
  const getPlainTextExcerpt = (article) => {
    if (article.excerpt) return article.excerpt;
    if (article.content) {
      // Strip HTML tags and get first 160 characters
      const plainText = article.content.replace(/<[^>]*>/g, '');
      return plainText.substring(0, 160) + (plainText.length > 160 ? '...' : '');
    }
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-3/4 mb-4" />
            <div className="h-64 bg-gray-700 rounded mb-6" />
            <div className="space-y-3">
              <div className="h-4 bg-gray-700 rounded w-full" />
              <div className="h-4 bg-gray-700 rounded w-full" />
              <div className="h-4 bg-gray-700 rounded w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SEO 
          title="Article Not Found"
          description="The requested article could not be found."
          noIndex={true}
        />
        <div className="text-center">
          <p className="text-gray-400 text-xl mb-4">{error || 'Article not found'}</p>
          <Link to="/news" className="text-sky-400 hover:text-sky-300 flex items-center gap-2 justify-center">
            <ArrowLeft className="w-4 h-4" /> Back to News
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="news-article-page" className="min-h-screen py-8 px-4">
      {/* SEO Meta Tags - use featured image directly for social sharing (like WordPress) */}
      <SEO 
        title={article.title}
        description={getPlainTextExcerpt(article)}
        image={article.featured_image ? resolveMediaUrl(article.featured_image) : `${BACKEND_URL}/api/og-image/news/${article.slug}`}
        url={`/news/${article.slug}`}
        type="article"
        article={{
          publishedTime: article.published_at || article.created_at,
          modifiedTime: article.updated_at,
          author: article.author || 'New Logical Sports',
          tags: article.league_ids?.map(lid => getLeagueName(lid)).filter(Boolean)
        }}
      />
      
      {/* Structured Data for Search Engines */}
      <ArticleStructuredData article={article} />
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/news" 
          className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to News
        </Link>

        {/* Article Header */}
        <article className="bg-gray-800/30 rounded-xl overflow-hidden border border-gray-700">
          {/* Featured Image */}
          {article.featured_image ? (
            <div className="h-72 md:h-96 relative">
              <img 
                src={resolveMediaUrl(article.featured_image)} 
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
            </div>
          ) : (
            <div className="h-48 bg-gradient-to-br from-sky-900 to-blue-900" />
          )}

          <div className="p-6 md:p-8">
            {/* League Tags */}
            {article.league_ids?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {article.league_ids.map(lid => (
                  <Badge key={lid} className="bg-sky-500/20 text-sky-300 border border-sky-500/30">
                    <Tag className="w-3 h-3 mr-1" />
                    {getLeagueName(lid)}
                  </Badge>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {article.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-gray-400 mb-8 pb-6 border-b border-gray-700">
              <div className="flex flex-wrap items-center gap-4">
                {article.author && (
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {article.author}
                  </span>
                )}
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(article.published_at || article.created_at)}
                </span>
              </div>
              <ShareButton slug={article.slug} title={article.title} />
            </div>

            {/* Excerpt */}
            {article.excerpt && (
              <p className="text-xl text-gray-300 mb-6 font-medium leading-relaxed">
                {article.excerpt}
              </p>
            )}

            {/* Content */}
            <div 
              className="text-gray-200 text-lg leading-relaxed space-y-4 [&_p]:mb-4 [&_h1]:text-white [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-3 [&_h2]:text-white [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:text-white [&_h3]:text-lg [&_h3]:font-semibold [&_a]:text-sky-400 [&_a]:underline [&_strong]:text-white [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allowfullscreen', 'frameborder', 'src', 'target'] }) }}
            />
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white mb-6">More News</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map(related => (
                <Link
                  key={related.id}
                  to={`/news/${related.slug}`}
                  className="group bg-gray-800/50 rounded-lg overflow-hidden border border-gray-700 hover:border-sky-500/50 transition-all"
                >
                  <div className="h-32 relative overflow-hidden">
                    {related.featured_image ? (
                      <img 
                        src={resolveMediaUrl(related.featured_image)} 
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-sky-900 to-blue-900" />
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-medium group-hover:text-sky-400 transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <p className="text-gray-500 text-xs mt-2">
                      {formatDate(related.published_at || related.created_at).split(',')[0]}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsArticlePage;
